


















































import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { ModelEditableMixin } from "../../../../../base/components/system";
import { OptionDropDownSelector } from "../../../../../base/components/system/selectors";
import { StandardOption } from "../../../../../base/components/system/selectors/mixins";
import { ILinkedScriptExecuteBehavior, IScriptExecuteParameter, ScriptExecuteTimingKinds, ILinkedPageObjectSpecifiable } from "../../../../domain/format/objects";
import { CommonComponentsContainer } from "../../../../../formatEditor/components/format/editors/contents";
import { FormatObjectSelectorColumnTypes } from "../../../../../formatEditor/components/format/editors/contents/commonComponents/selectors";
import { IExternalScriptSpecification, IFormatObjectEditable } from "../../../../../formatEditor/domain/editableFormat";

@Component({
  components: {
    OptionDropDownSelector,
  }
})
export default class ScriptExecuteBehaviorEditor extends Mixins(ModelEditableMixin) {
  // ModelEditableMixinと同時に使う事.ModelEditableMixin利用APIの宣言.
  writePropertyWhenValid: (writer: (source: ILinkedScriptExecuteBehavior) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: ILinkedScriptExecuteBehavior) => T, defaultValue: T) => T;

  @Prop()
  commonComponents: CommonComponentsContainer;

  get timing(): ScriptExecuteTimingKinds {
    return this.readPropertyOrDefault(s => s.timing, ScriptExecuteTimingKinds.None);
  }

  set timing(value: ScriptExecuteTimingKinds) {
    this.writePropertyWhenValid(s => s.timing = value);
  }

  get timingOptions(): StandardOption<ScriptExecuteTimingKinds>[] {
    return [
      { label: "自動更新しない", value: ScriptExecuteTimingKinds.None, description: "", },
      { label: "文書登録時", value: ScriptExecuteTimingKinds.DocumentRegistered, description: "", },
      { label: "文書登録・一時保存時", value: ScriptExecuteTimingKinds.DocumentRegisteredAsAny, description: "", },
      { label: "計算時", value: ScriptExecuteTimingKinds.Calculated, description: "", },
      { label: "クリック時", value: ScriptExecuteTimingKinds.Clicked, description: "", },
    ];
  }

  get scriptPath(): string {
    return this.readPropertyOrDefault(s => s.scriptPath, "");
  }

  get separator(): string {
    return this.readPropertyOrDefault(s => s.separator, "");
  }

  get parameters(): IScriptExecuteParameter[] {
    return this.readPropertyOrDefault(s => s.parameters, []);
  }

  get selectableExternalScripts(): IExternalScriptSpecification[] {
    return this.commonComponents.readPropertyOrDefault(s => s.getSpecification().externalScripts, []);
  }

  get csvFilePathFormat(): string {
    return this.readPropertyOrDefault(s => s.csvFilePathFormat, "");
  }

  set csvFilePathFormat(value: string) {
    this.writePropertyWhenValid(s => s.csvFilePathFormat = value);
  }

  get valuesXmlPathFormat(): string {
    return this.readPropertyOrDefault(s => s.valuesXmlPathFormat, "");
  }

  set valuesXmlPathFormat(value: string) {
    this.writePropertyWhenValid(s => s.valuesXmlPathFormat = value);
  }

  get previousValuesCsvFilePathFormat(): string {
    return this.readPropertyOrDefault(s => s.previousValuesCsvFilePathFormat, "");
  }

  set previousValuesCsvFilePathFormat(value: string) {
    this.writePropertyWhenValid(s => s.previousValuesCsvFilePathFormat = value);
  }

  get references(): ILinkedPageObjectSpecifiable[] {
    return this.readPropertyOrDefault(s => s.references, []);
  }

  set references(value: ILinkedPageObjectSpecifiable[]) {
    this.writePropertyWhenValid(s => s.references = value);
  }

  get selectionObjects(): ILinkedPageObjectSpecifiable[] {
    return Enumerable.from(this.commonComponents.allPagesFormatObjectsForEachPage)
      .selectMany(p => Enumerable.from(p.objects)
        .where(o => this.conditionsOfEachElementType(o))
        .select(o => this.toLinkedPageObjectSpecifiable(o, p.pageKey))
        .toArray())
      .toArray();
  }

  private conditionsOfEachElementType(object: IFormatObjectEditable): boolean {
    const linkedObject = object.toLinkedObjectIdentifier();
    switch (object.objectTypeName().getValue()) {
      case "text":
      case "date":
      case "tag":
      case "combo":
      case "roll":
      case "script":
      case "imagearea":
        return !!linkedObject.id && !!linkedObject.key && linkedObject.key !== "";
      case "check":
      case "circleselect":
        return !!linkedObject.id && !!linkedObject.key && linkedObject.key !== "" && !!linkedObject.subKey && linkedObject.subKey !== "";
      default:
        return false;
    }
  }

  private toLinkedPageObjectSpecifiable(object: IFormatObjectEditable, pageKey: number): ILinkedPageObjectSpecifiable {
    const linkedObject = object.toLinkedObjectIdentifier();
    return {
      pageKey: pageKey,
      type: linkedObject.type,
      id: linkedObject.id,
      key: linkedObject.key,
      subKey: linkedObject.subKey,
      tableIndex: linkedObject.tableIndex,
      isDate: linkedObject.isDate,
    };
  }

  async showScriptPathEditDialog() {
    const dialog = this.commonComponents.getDialogs().getScriptPathEditDialog();
    const parameter = await dialog.show({
      scriptPath: this.scriptPath,
      separator: this.separator,
      parameters: this.parameters,
    }, this.selectableExternalScripts);
    if (!parameter) { return; }
    this.writePropertyWhenValid(s => {
      s.scriptPath = parameter.scriptPath;
      s.separator = parameter.separator;
      s.parameters = parameter.parameters;
    });
  }

  async showCsvFilePathFormatEditDialog() {
    const dialog = this.commonComponents.getDialogs().getFilePathEditDialog();
    const filePath = await dialog.show(this.csvFilePathFormat);
    if (!filePath) { return; }
    this.csvFilePathFormat = filePath;
  }

  async showPreviousValuesCsvFilePathFormatEditDialog() {
    const dialog = this.commonComponents.getDialogs().getFilePathEditDialog();
    const filePath = await dialog.show(this.previousValuesCsvFilePathFormat);
    if (!filePath) { return; }
    this.previousValuesCsvFilePathFormat = filePath;
  }

  async showValuesXmlPathFormatEditDialog() {
    const dialog = this.commonComponents.getDialogs().getFilePathEditDialog();
    const filePath = await dialog.show(this.valuesXmlPathFormat);
    if (!filePath) { return; }
    this.valuesXmlPathFormat = filePath;
  }

  async showReferencesEditDialog() {
    const dialog = this.commonComponents.getDialogs().getFormatObjectSelectDialog();
    const references = await dialog.show(
      this.references,
      this.selectionObjects,
      ["selection", "index", "type", "key", "subKey", "id", "pageNumber"],
      ["selection", "type", "key", "subKey", "id", "pageNumber"],
      true,
      false,
      [
        { enabled: false, name: "テキスト", description: "テキストで絞り込みします", filter: v => v.type === "text" || v.type === "tag" },
        { enabled: false, name: "日付", description: "日付で絞り込みします", filter: v => v.type === "date" && v.isDate === true },
        { enabled: false, name: "時間", description: "時間で絞り込みします", filter: v => v.type === "date" && v.isDate === false },
        { enabled: false, name: "チェック", description: "チェックで絞り込みします", filter: v => v.type === "check" },
        { enabled: false, name: "円選択", description: "円選択で絞り込みします", filter: v => v.type === "circleselect" },
        { enabled: false, name: "リスト", description: "リストで絞り込みします", filter: v => v.type === "combo" },
        { enabled: false, name: "クリック", description: "クリック選択で絞り込みします", filter: v => v.type === "roll" },
        { enabled: false, name: "外部データ", description: "外部データで絞り込みします", filter: v => v.type === "script" },
        { enabled: false, name: "画像", description: "画像で絞り込みします", filter: v => v.type === "imagearea" },
      ],
      this.toCellString);
    if (!references) { return; }
    this.references = references;
  }

  private toCellString(column: FormatObjectSelectorColumnTypes, row: ILinkedPageObjectSpecifiable): string {
    const formatObject = this.getFormatObjectEditable(row);
    switch (column) {
      case "type":
        return formatObject ? formatObject.getTypeName() + (!!row.tableIndex ? "[" + row.tableIndex.row + "][" + row.tableIndex.column + "]" : "") : "";
      case "id":
        return row.id ? row.id.toString() : "";
      case "key":
        return row.key; // formatObject.getIdentifiableName()を出したほうが良いと思われるが要検討
      case "subKey":
        return row.subKey ? row.subKey : "";
      case "pageNumber":
        const pageNumber = this.commonComponents.getPageNumber(row.pageKey);
        return pageNumber === 0 ? "削除済" : pageNumber.toString();
      default:
        return "";
    }
  }
  
  private getFormatObjectEditable(row: ILinkedPageObjectSpecifiable): IFormatObjectEditable {
    const pageFormatObjects = this.commonComponents.allPagesFormatObjectsForEachPage.find(p => p.pageKey === row.pageKey);
    if (!pageFormatObjects) {
      return null;
    }
    const rRow = row.tableIndex ? row.tableIndex.row : null;
    const rColumn = row.tableIndex ? row.tableIndex.column : null;
    return Enumerable.from(pageFormatObjects.objects)
      .firstOrDefault(o => {
        const linkedObject = o.toLinkedObjectIdentifier();
        const linkedRow = linkedObject.tableIndex ? linkedObject.tableIndex.row : null;
        const linkedColumn = linkedObject.tableIndex ? linkedObject.tableIndex.column : null;
        return linkedObject.type === row.type &&
          linkedObject.id === row.id &&
          linkedObject.key === row.key &&
          linkedObject.subKey === row.subKey &&
          linkedRow === rRow &&
          linkedColumn === rColumn &&
          linkedObject.isDate === row.isDate;
      });
  }
}
